import React, { useState } from 'react';
import {
  waiveRequestFee, flagRequestFeeRefundDue, markRequestFeeRefunded,
  adminFeeStatusLabel, adminFeeBadgeTone, daysToDeadline,
} from '../../lib/adminRequestFees';

const fmt = (iso, isAr) => (iso
  ? new Date(iso).toLocaleString(isAr ? 'ar-u-ca-gregory' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—');

// One request's fee, and the only three things an admin can do to it. Each
// action asks for its reason / Telr reference in the panel itself, never a
// browser prompt, and reloads from the server afterwards (onChanged).
export default function RequestFeePanel({ fee, requestId, isAr, onChanged, flash }) {
  const [mode, setMode] = useState(null);   // 'waive' | 'flag' | 'refund'
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  if (!fee) return null;
  const days = daysToDeadline(fee);

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      if (mode === 'waive') await waiveRequestFee(requestId, text);
      if (mode === 'flag') await flagRequestFeeRefundDue(requestId, text);
      if (mode === 'refund') await markRequestFeeRefunded(requestId, text, note);
      setMode(null); setText(''); setNote('');
      flash?.(isAr ? 'تم الحفظ' : 'Saved');
      await onChanged?.();
    } catch (e) {
      setErr(e?.message || (isAr ? 'تعذّر الحفظ' : 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  const row = (k, v) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12.5, padding: '3px 0' }}>
      <span style={{ color: 'var(--ac-muted)' }}>{k}</span><span style={{ color: 'var(--ac-ink)', textAlign: 'end' }}>{v}</span>
    </div>
  );

  return (
    <div className="ac-card" style={{ padding: 14, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ac-ink)' }}>
          {isAr ? 'رسوم تأكيد الطلب' : 'Request confirmation fee'} · {fee.amount} {isAr ? 'ر.س' : 'SAR'}
        </div>
        <span className={`ac-badge ${adminFeeBadgeTone(fee.status)}`}><span className="dot" />{adminFeeStatusLabel(fee.status, isAr)}</span>
      </div>

      {row(isAr ? 'دُفعت' : 'Paid', fmt(fee.paid_at, isAr))}
      {fee.telr_ref && row(isAr ? 'مرجع Telr' : 'Telr ref', <span dir="ltr">{fee.telr_ref}</span>)}
      {fee.refund_deadline_at && row(
        isAr ? 'مهلة ١٠ أيام عمل' : '10-working-day deadline',
        <>{fmt(fee.refund_deadline_at, isAr)}{fee.status === 'paid' && days !== null ? ` · ${days > 0 ? (isAr ? `باقي ${days} يوم` : `${days} day(s) left`) : (isAr ? 'انتهت' : 'passed')}` : ''}</>,
      )}
      {fee.credited_at && row(isAr ? 'خُصمت من الدفعة الأولى' : 'Credited on first payment', `${fee.credited_amount} · ${fmt(fee.credited_at, isAr)}`)}
      {fee.refund_due_at && row(isAr ? 'استحقت الاسترداد' : 'Refund due since', `${fmt(fee.refund_due_at, isAr)} · ${fee.refund_due_reason || ''}`)}
      {fee.refunded_at && row(isAr ? 'رُدّت' : 'Refunded', <>{fmt(fee.refunded_at, isAr)} · <span dir="ltr">{fee.refund_reference}</span></>)}
      {fee.waived_at && row(isAr ? 'أُعفيت' : 'Waived', `${fmt(fee.waived_at, isAr)} · ${fee.waive_reason || ''}`)}

      {fee.status === 'refund_due' && (
        <p style={{ fontSize: 12, color: 'var(--ac-muted)', margin: '8px 0 0', lineHeight: 1.7 }}>
          {isAr
            ? 'نفّذ الاسترداد يدويًا من لوحة Telr أولًا، ثم سجّل مرجع الاسترداد هنا. لا شيء في النظام يرد المال تلقائيًا.'
            : 'Refund it by hand in the Telr merchant panel first, then record the refund reference here. Nothing in the system refunds money automatically.'}
        </p>
      )}

      {!mode && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          {fee.status === 'awaiting_payment' && (
            <button className="ac-btn ac-btn-sm" onClick={() => setMode('waive')}>{isAr ? 'إعفاء من الرسوم' : 'Waive fee'}</button>
          )}
          {fee.status === 'paid' && (
            <button className="ac-btn ac-btn-sm" onClick={() => setMode('flag')}>{isAr ? 'تحويل إلى «مستحقة الاسترداد»' : 'Flag refund due'}</button>
          )}
          {fee.status === 'refund_due' && (
            <button className="ac-btn ac-btn-sm ac-btn-primary" onClick={() => setMode('refund')}>{isAr ? 'تسجيل الاسترداد' : 'Record refund'}</button>
          )}
        </div>
      )}

      {mode && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          <input className="ac-input" value={text} onChange={(e) => setText(e.target.value)} dir={mode === 'refund' ? 'ltr' : undefined}
            placeholder={mode === 'refund' ? (isAr ? 'مرجع الاسترداد في Telr (إلزامي)' : 'Telr refund reference (required)') : (isAr ? 'السبب (إلزامي)' : 'Reason (required)')} />
          {mode === 'refund' && (
            <input className="ac-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={isAr ? 'ملاحظة (اختياري)' : 'Note (optional)'} />
          )}
          {err && <div style={{ fontSize: 12, color: 'var(--ac-danger)' }}>{err}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="ac-btn ac-btn-sm ac-btn-primary" disabled={busy || !text.trim()} onClick={submit}>{isAr ? 'تأكيد' : 'Confirm'}</button>
            <button className="ac-btn ac-btn-sm" disabled={busy} onClick={() => { setMode(null); setText(''); setNote(''); setErr(''); }}>{isAr ? 'إلغاء' : 'Cancel'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
